// ============================================================
// MessageTextarea.tsx — (batch 2) the covering-message box of the send
// windows (bill, credit note, settlement statement): about 8 lines tall to
// start, it grows with what's typed (no inner scrollbar for a normal email)
// up to a sensible maximum, then scrolls. Every other textarea prop passes
// straight through (id, aria-*, disabled, maxLength …).
// ============================================================
import React, { useLayoutEffect, useRef } from 'react'

export const MESSAGE_ROWS = 8
// Beyond this the box scrolls instead of pushing the window taller.
const MAX_HEIGHT_PX = 480

type Props = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'> & { value: string }

const MessageTextarea: React.FC<Props> = ({ value, className = '', style, ...rest }) => {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // Measure from the natural (rows-based) height, then grow to the content.
    el.style.height = 'auto'
    const natural = el.clientHeight
    const want = el.scrollHeight
    if (!want || want <= natural) { el.style.overflowY = ''; return } // nothing to grow (or no layout, e.g. jsdom)
    const border = el.offsetHeight - el.clientHeight
    el.style.height = `${Math.min(want + border, MAX_HEIGHT_PX)}px`
    el.style.overflowY = want + border > MAX_HEIGHT_PX ? 'auto' : 'hidden'
  }, [value])
  return (
    <textarea ref={ref} rows={MESSAGE_ROWS} value={value} style={{ height: 'auto', ...style }}
      className={`${className} py-[9px] leading-[1.5] resize-y`} {...rest} />
  )
}

export default MessageTextarea
